import React, { useMemo } from 'react';
import { CrmInput, CrmSelect } from '../ui';
import { CITIES_BY_STATE_CODE, COUNTRIES, GLOBAL_DEFAULTS, STATES_BY_COUNTRY } from './settingsFormUtils';

const countryCodeFor = (value) => COUNTRIES.find((country) => country.code === value || country.name === value)?.code || GLOBAL_DEFAULTS.countryCode || '';
const countryNameFor = (code) => COUNTRIES.find((country) => country.code === code)?.name || code || '';
const normalizeName = (value) => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
export const stateCodeFor = (countryCode, value) => (STATES_BY_COUNTRY[countryCode] || []).find((state) => state.code === value || state.name === value || normalizeName(state.name) === normalizeName(value))?.code || '';

export function CityInput({ country, state, value, onChange, label = 'City', ...props }) {
  const countryCode = countryCodeFor(country);
  const stateCode = stateCodeFor(countryCode, state);
  const options = useMemo(() => CITIES_BY_STATE_CODE[stateCode] || [], [stateCode]);

  if (options.length) {
    return (
      <CrmSelect
        {...props}
        label={label}
        value={value || ''}
        autoComplete="address-level2"
        onChange={onChange}
        helperText={props.helperText || 'Choose a city for the selected state.'}
      >
        <option value="">Select city</option>
        {options.map((city) => <option key={city} value={city}>{city}</option>)}
      </CrmSelect>
    );
  }

  return (
    <CrmInput
      {...props}
      label={label}
      value={value || ''}
      autoComplete="address-level2"
      onChange={onChange}
      helperText={props.helperText || (state ? 'No shared city list is available for this state; enter the city.' : 'Select a state first.')}
      disabled={!state && Boolean(countryCode)}
    />
  );
}

export default function LocationStateCityFields({
  country,
  state,
  city,
  onChange,
  showCountry = true,
  countryLabel = 'Country',
  stateLabel = 'State / Province',
  cityLabel = 'City',
}) {
  const countryCode = countryCodeFor(country);
  const states = STATES_BY_COUNTRY[countryCode] || [];
  const selectedStateCode = stateCodeFor(countryCode, state);
  const update = (key, nextValue) => onChange?.(key, nextValue);

  return (
    <>
      {showCountry ? (
        <CrmSelect
          label={countryLabel}
          value={countryCode}
          onChange={(event) => {
            update('country', countryNameFor(event.target.value));
            update('state', '');
            update('city', '');
          }}
        >
          {COUNTRIES.map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}
        </CrmSelect>
      ) : null}
      {states.length ? (
        <CrmSelect
          label={stateLabel}
          value={selectedStateCode}
          onChange={(event) => {
            const selected = states.find((item) => item.code === event.target.value);
            update('state', selected?.name || '');
            update('city', '');
          }}
        >
          <option value="">Select state / province</option>
          {states.map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}
        </CrmSelect>
      ) : (
        <CrmInput label={stateLabel} value={state || ''} onChange={(event) => update('state', event.target.value)} />
      )}
      <CityInput country={country} state={state} label={cityLabel} value={city || ''} onChange={(event) => update('city', event.target.value)} />
    </>
  );
}
